import React, { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

// Small pieces every weather part shares: the table toggle each chart carries,
// the chips, the tooltip look (TimeAnalysisPage's: 10px radius, hairline, DM Sans 12),
// and a measured width for the charts drawn in plain SVG.

export const NOTE = 'font-sans text-[11px] leading-[1.5] text-[#6B6670]';
export const MICRO = 'font-sans text-[10px] font-medium leading-[1.4] text-[#6B6670]';
export const FOCUS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4A2D5E] focus-visible:ring-offset-2';
export const TIP_CLASS = 'pointer-events-none rounded-[10px] border border-[#E6E2DE] bg-white px-3 py-2 font-sans text-xs text-[#2D2A2E] shadow-card';
export const TIP_STYLE = { borderRadius: '10px', border: '1px solid #E6E2DE', fontFamily: 'DM Sans', fontSize: 12 };
export const TH = 'py-1.5 pr-3 font-medium text-[#6B6670] text-left whitespace-nowrap';
export const TH_NUM = 'py-1.5 pl-3 font-medium text-[#6B6670] text-right whitespace-nowrap';
export const TD = 'py-1.5 pr-3 text-[#2D2A2E] whitespace-nowrap';
export const TD_NUM = 'py-1.5 pl-3 text-[#2D2A2E] text-right tabular-nums whitespace-nowrap';

export type ChipTone = 'slower' | 'busier' | 'plum' | 'quiet';
const CHIP_TONE: Record<ChipTone, string> = {
  slower: 'bg-[#B54A4A]/10 text-[#B54A4A]',
  busier: 'bg-[#2E6B4F]/10 text-[#2E6B4F]',
  plum: 'bg-[#F0EBF5] text-[#4A2D5E]',
  quiet: 'border border-[#E6E2DE] text-[#6B6670]',
};

export function Chip({ tone, kind, children }: { tone: ChipTone; kind?: string; children: ReactNode }) {
  return (
    <span data-chip={kind} className={`inline-flex items-center rounded-badge px-2 py-0.5 font-sans text-[10px] font-medium leading-[1.4] whitespace-nowrap ${CHIP_TONE[tone]}`}>
      {children}
    </span>
  );
}

/** "Show the table": the same data as the chart above it, as a real table. */
export function TableToggle({ children, className = '' }: { children: ReactNode; className?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={className}>
      <button
        type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)}
        className={`rounded-[8px] font-sans text-xs font-medium text-[#4A2D5E] underline-offset-4 hover:underline ${FOCUS}`}
      >
        {open ? 'Hide the table' : 'Show the table'}
      </button>
      {open && <div className="mt-2 max-w-full overflow-x-auto">{children}</div>}
    </div>
  );
}

/** The element's laid-out width, with a fallback for the first paint and for tests. */
export function useWidth<T extends HTMLElement>(fallback: number): [React.RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const read = () => { if (el.clientWidth > 0) setWidth(el.clientWidth); };
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}
